import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Mail, Link as LinkIcon, QrCode, Share2, ChevronRight, ArrowLeft } from 'lucide-react';
export function CAGuidePage() {
  const [checklist, setChecklist] = useState([false, false, false]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [progress, setProgress] = useState(0);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCheck = (index: number) => {
    const newChecklist = [...checklist];
    newChecklist[index] = !newChecklist[index];
    setChecklist(newChecklist);
    setHasInteracted(true);
    // Update progress based on checklist (up to 50% before completion)
    const completedCount = newChecklist.filter(Boolean).length;
    if (!isCompleted) {
      setProgress(Math.round(completedCount / newChecklist.length * 50));
    }
  };
  const handleComplete = () => {
    setIsCompleted(true);
    setProgress(100);
    // Small delay to allow render before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen w-full bg-white text-[#1a0647]" data-id="element-722">
      {/* Mandatory Typography Imports & Classes */}
      <style data-id="element-723">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&display=swap');
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Simple Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-724">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-725">
          <Link to="/resources" className="text-gray-600 hover:text-[#8528e2] font-manrope font-semibold text-base transition-colors flex items-center gap-2 min-h-[44px] min-w-[44px]" data-id="element-726">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-727" />
            Back to Resources
          </Link>
          <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo Logo" className="h-6 w-auto" data-id="element-728" />
        </div>
      </header>

      <main data-id="element-729">
        {/* 1. Module Header (Hero Block) */}
        <section className="bg-[#1a0647] text-white py-12 md:py-16 px-4 md:px-8" data-id="element-730">
          <div className="max-w-3xl mx-auto" data-id="element-731">
            <h1 className="font-manrope text-3xl md:text-5xl font-bold mb-4 text-white leading-tight" data-id="element-732">
              The California Resident's Guide to Privacy & Opt-Out
            </h1>
            <p className="font-inter text-lg md:text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-733">
              Learn how to use California's new DROP tool and OOPS signal to
              stop data brokers from selling your family's personal information.
            </p>

            <div className="inline-flex items-center gap-2 bg-[#01ced1] text-[#1a0647] px-4 py-2 rounded-full font-manrope font-bold text-base mb-10 min-h-[44px]" data-id="element-734">
              <span aria-hidden="true" data-id="element-735">⏱️</span> 12 minutes to complete
            </div>

            <div className="space-y-3 bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-736">
              <div className="flex justify-between font-manrope font-semibold text-base" data-id="element-737">
                <span id="progress-label" data-id="element-738">Your progress</span>
                <span data-id="element-739">{progress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-labelledby="progress-label" data-id="element-740">
                <div className="bg-[#214ae9] h-full transition-all duration-700 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-741" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. The Hook (Story Block) */}
        <section className="bg-[#eae6dc] py-12 md:py-16 px-4 md:px-8" data-id="element-742">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-6 items-start" data-id="element-743">
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-3xl border border-gray-200" aria-hidden="true" data-id="element-744">
              🏠
            </div>
            <div className="flex-1 space-y-4" data-id="element-745">
              <p className="font-inter text-gray-700 text-lg leading-relaxed" data-id="element-746">
                Imagine opening your mailbox to find a catalog for a medical
                condition you only searched for once, late at night. Or worse,
                your teenager starts receiving texts from marketers who somehow
                know their exact age and location. Data brokers quietly collect
                and sell this information every day.
              </p>
              <p className="font-inter text-[#1a0647] text-xl font-bold leading-relaxed" data-id="element-747">
                In this module, you will learn exactly how to use California's
                free tools to force these companies to delete your family's
                data.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Concept Cards (One Idea Per Card) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-748">
          <div className="max-w-4xl mx-auto" data-id="element-749">
            <h2 className="sr-only" data-id="element-750">Key Concepts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-751">
              {/* Card 1 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-752">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-753">
                  The DROP Tool
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-754">
                  The Delete Request and Opt-Out Platform (DROP) is a free state
                  tool that sends a single deletion request to over 500
                  registered data brokers at once. Instead of emailing hundreds
                  of companies, you fill out one form.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-755">
                  Think of this like a universal "Do Not Call" registry, but for
                  your personal data.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-756">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-757">
                  The OOPS Signal
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-758">
                  The Opt-Out Preference Signal (OOPS) is a setting in your web
                  browser that automatically tells every website you visit not
                  to sell or share your data. You turn it on once, and it works
                  quietly in the background.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-759">
                  Think of this like a digital "No Soliciting" sign permanently
                  hung on your front door.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-760">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-761">
                  Data Brokers
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-762">
                  These are companies that collect your public records, purchase
                  history, and online activity, package it into a profile, and
                  sell it to advertisers or other businesses.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-763">
                  Think of them like digital gossips who profit from sharing
                  your family's secrets.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-764">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-765">
                  Your Right to Delete
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-766">
                  Under California law, you have the right to demand that
                  businesses delete the personal information they have collected
                  about you, with a few legal exceptions.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-767">
                  Think of this like having the legal authority to shred someone
                  else's file on you.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Practice Activity (Interactive Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white border-t border-gray-100" data-id="element-768">
          <div className="max-w-3xl mx-auto" data-id="element-769">
            <h2 className="font-manrope text-[#1a0647] text-3xl md:text-4xl font-bold mb-6" data-id="element-770">
              Try It Now
            </h2>
            <p className="font-inter text-gray-700 text-lg mb-10 leading-relaxed" data-id="element-771">
              Before you submit your official request, let's make sure you have
              everything you need. Tap each step as you complete it.
            </p>

            <div className="space-y-4" role="group" aria-label="Preparation Checklist" data-id="element-772">
              {['Check if your current web browser supports the OOPS signal (like Brave or Firefox) or if you need an extension.', "Gather the information you'll need for DROP: your full name, previous addresses, and old email accounts.", "Locate your Mobile Advertising ID (MAID) in your phone's privacy settings."].map((item, i) => <button key={i} onClick={() => handleCheck(i)} className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 flex items-start gap-5 min-h-[44px] ${checklist[i] ? 'border-[#01ced1] bg-[#01ced1]/5 shadow-sm' : 'border-gray-200 bg-[#f8f8f8] hover:border-[#8528e2]/40 hover:bg-white'}`} aria-pressed={checklist[i]} data-id="element-773">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 mt-0.5 transition-colors ${checklist[i] ? 'bg-[#01ced1] border-[#01ced1] text-white' : 'border-gray-300 text-transparent bg-white'}`} data-id="element-774">
                    <CheckCircle2 size={20} aria-hidden="true" data-id="element-775" />
                  </div>
                  <span className={`font-inter text-lg leading-relaxed ${checklist[i] ? 'text-[#1a0647] font-semibold' : 'text-gray-700'}`} data-id="element-776">
                    {item}
                  </span>
                </button>)}
            </div>
          </div>
        </section>

        {/* 5. Honesty Gate (Pre-Commitment Block) */}
        <section className="py-8 px-4 md:px-8 bg-white" data-id="element-777">
          <div className="max-w-3xl mx-auto" data-id="element-778">
            <div className="border-l-4 border-[#8528e2] bg-[#8528e2]/5 p-6 md:p-8 rounded-r-xl mb-8" data-id="element-779">
              <p className="font-inter text-[#1a0647] text-lg leading-relaxed font-medium" data-id="element-780">
                Before you confirm: this step matters for your family's
                protection. Please only mark it complete if you actually did it.
                It's completely okay to come back to this later.
              </p>
            </div>

            {hasInteracted && !isCompleted && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-781">
                <button onClick={handleComplete} className="bg-[#8528e2] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#b063ff] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center w-full md:w-auto shadow-md" data-id="element-782">
                  I completed this step
                </button>
              </div>}
          </div>
        </section>

        {/* 6. Reflection Prompt (Personal Connection Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-783">
          <div className="max-w-3xl mx-auto" data-id="element-784">
            <div className="bg-[#f8f8f8] border border-gray-200 p-8 md:p-10 rounded-2xl shadow-sm" data-id="element-785">
              <div className="inline-block bg-[#eae6dc] text-[#1a0647] font-manrope font-bold px-4 py-1.5 rounded-md text-sm mb-6 uppercase tracking-wider" data-id="element-786">
                Reflect for a Moment
              </div>
              <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold mb-8 leading-tight" data-id="element-787">
                Which of these steps feels hardest for you right now, and what's
                one thing that might make it easier?
              </h3>
              <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} placeholder="Type your thoughts here..." className="w-full bg-white border border-gray-300 rounded-xl p-5 font-inter text-gray-700 text-lg min-h-[160px] focus:outline-none focus:ring-2 focus:ring-[#8528e2] focus:border-transparent resize-y shadow-inner" aria-label="Reflection input" data-id="element-788" />
            </div>
          </div>
        </section>

        {/* 7. Takeaway Block (Action Anchor) */}
        <section className="bg-[#1a0647] text-white py-20 px-4 md:px-8" data-id="element-789">
          <div className="max-w-3xl mx-auto text-center" data-id="element-790">
            <p className="font-inter text-2xl md:text-3xl font-bold leading-relaxed mb-12" data-id="element-791">
              The single most important action you can take today is to submit
              your DROP request at consumer.drop.privacy.ca.gov.
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-792">
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-793">
                <Printer size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-794" />
                <span className="font-manrope text-sm font-semibold" data-id="element-795">
                  Print
                </span>
              </button>
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-796">
                <Mail size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-797" />
                <span className="font-manrope text-sm font-semibold" data-id="element-798">
                  Email to myself
                </span>
              </button>
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-799">
                <LinkIcon size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-800" />
                <span className="font-manrope text-sm font-semibold" data-id="element-801">
                  Copy link
                </span>
              </button>
              <button className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-802">
                <QrCode size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-803" />
                <span className="font-manrope text-sm font-semibold" data-id="element-804">
                  QR code
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* 8. Completion Confirmation */}
        {isCompleted && <section className="bg-[#01ced1] py-16 md:py-24 px-4 md:px-8 animate-in fade-in slide-in-from-bottom-8 duration-1000" data-id="element-805">
            <div className="max-w-3xl mx-auto text-center" data-id="element-806">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-807">
                <CheckCircle2 size={40} className="text-[#01ced1]" aria-hidden="true" data-id="element-808" />
              </div>
              <h2 className="font-manrope text-[#1a0647] text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-809">
                Nice work. The California Privacy module is complete. Your
                family is a little safer today.
              </h2>

              <div className="bg-white/30 p-8 rounded-2xl mt-10 max-w-lg mx-auto backdrop-blur-sm border border-white/40 shadow-sm" data-id="element-810">
                <p className="font-inter text-[#1a0647] text-xl font-bold mb-6" data-id="element-811">
                  Share with your family
                </p>
                <button className="bg-[#1a0647] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#2a0a6b] transition-colors min-h-[44px] inline-flex items-center justify-center gap-3 shadow-md w-full sm:w-auto" data-id="element-812">
                  <Share2 size={20} aria-hidden="true" data-id="element-813" />
                  Send to a family member
                </button>
              </div>

              <div className="mt-16 text-left max-w-2xl mx-auto" data-id="element-814">
                <Link to="/resources" className="block bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all group no-underline border-2 border-transparent hover:border-[#8528e2]/50" data-id="element-815">
                  <p className="font-manrope text-[#8528e2] font-bold text-sm uppercase tracking-wider mb-3" data-id="element-816">
                    What's Next
                  </p>
                  <div className="flex items-center justify-between" data-id="element-817">
                    <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold" data-id="element-818">
                      Protecting Your Passwords
                    </h3>
                    <div className="w-12 h-12 rounded-full bg-[#f8f8f8] flex items-center justify-center group-hover:bg-[#8528e2] group-hover:text-white transition-colors text-[#1a0647] shadow-sm" data-id="element-819">
                      <ChevronRight size={24} aria-hidden="true" data-id="element-820" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>}
      </main>
    </div>;
}